import { TextField, Grid, Box, FormHelperText, FormControl } from '@ori-ui/mui';
import type { GlobalSharedMetadataValidationStringValidationDto } from '@ori/global-api-client-react';
import type { StaticSharedMetadataPasswordRequirementsDto } from '@ori/static-api-client-react';
import useTestId from '@ori/testid-generator';
import { useState } from 'react';
import type { Dispatch, ReactElement, SetStateAction, FC } from 'react';
import type { ControllerFieldState } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { Localization } from '../../../Localization';
import { defaultTranslations } from '../../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useFormError } from '../../../contexts';
import { getErrorLocalizationByCode, renderValidationMessage } from '../../../utils';
import { PasswordToggle } from '../../PasswordToggle';
import { PasswordRequirements } from './PasswordRequirements';

interface ITextFormFieldByConfigProps {
  name: string;
  label?: ReactElement | string;
  isEditable?: boolean;
  isAvailable?: boolean;
  displayOrder?: number;
  validation?: GlobalSharedMetadataValidationStringValidationDto;
  requirements?: StaticSharedMetadataPasswordRequirementsDto;
  fieldState?: ControllerFieldState;
  initialValue?: string;
  customPlaceholder?: string;
}

export interface IPasswordRequirement {
  key: keyof typeof defaultTranslations;
  validationRegex: RegExp;
}

const getPasswordRequirements = (
  minLength: number,
  maxLength: number,
  configRequirements?: StaticSharedMetadataPasswordRequirementsDto,
) => {
  const correctRequirements: IPasswordRequirement[] = [];

  if (configRequirements?.letter?.isRequired) {
    correctRequirements.push({
      key: 'passwordRequirementLetter',
      validationRegex: new RegExp(configRequirements.letter.regularExpression ?? ''),
    });
  }

  if (configRequirements?.lowercaseLetter?.isRequired) {
    correctRequirements.push({
      key: 'passwordRequirementLowerCaseLetter',
      validationRegex: new RegExp(configRequirements.lowercaseLetter.regularExpression ?? ''),
    });
  }

  if (configRequirements?.uppercaseLetter?.isRequired) {
    correctRequirements.push({
      key: 'passwordRequirementUpperCaseLetter',
      validationRegex: new RegExp(configRequirements.uppercaseLetter.regularExpression ?? ''),
    });
  }

  correctRequirements.push({
    key: 'passwordRequirementCharactersCount',
    validationRegex: new RegExp(`^.{${minLength},${maxLength}}$`),
  });

  if (configRequirements?.digit?.isRequired) {
    correctRequirements.push({
      key: 'passwordRequirementDigit',
      validationRegex: new RegExp(configRequirements.digit.regularExpression ?? ''),
    });
  }

  if (configRequirements?.specialCharacter?.isRequired) {
    correctRequirements.push({
      key: 'passwordRequirementSpecialCharacter',
      validationRegex: new RegExp(configRequirements.specialCharacter.regularExpression ?? ''),
    });
  }

  return correctRequirements;
};

export const PasswordFieldByConfig: FC<ITextFormFieldByConfigProps> = (props) => {
  const {
    name,
    label,
    isEditable,
    isAvailable,
    displayOrder,
    validation,
    requirements,
    initialValue,
    customPlaceholder,
  } = props;

  const [isPasswordVisible, setIsPasswordVisible]: [boolean, Dispatch<SetStateAction<boolean>>] = useState(false);

  const [isShowRequirements, setIsShowRequirements] = useState(false);
  const [isValidatingRequirements, setIsValidatingRequirements] = useState(false);

  const {
    context: { errorData },
  } = useFormError();
  const { customErrors } = errorData;

  const { getTestIdProps, getTestId } = useTestId();

  const submitError = customErrors?.find((error) => error.fieldName?.toLowerCase() === name.toLowerCase());

  const errorMessage = getErrorLocalizationByCode(submitError?.errorCode, name);
  const minLength = validation?.minLength ?? 6;
  const maxLength = validation?.maxLength ?? 255;

  const passwordRequirementsArray = getPasswordRequirements(minLength, maxLength, requirements);

  const regexPattern = validation?.regularExpression ? new RegExp(validation.regularExpression) : undefined;

  // we need start tabindex from 1 but displayOrder starts from 0
  const correctTabIndex = typeof displayOrder === 'number' ? displayOrder + 1 : displayOrder;

  const renderField = () => (
    <Grid
      item
      xs={12}
      sm={12}
      style={{ order: displayOrder }}
    >
      <Box
        margin="0 auto 20px"
        maxWidth={500}
      >
        <Controller
          name={name}
          rules={{
            required: validation?.isMandatory,
            pattern: regexPattern,
          }}
          defaultValue={initialValue}
          render={({ field, fieldState }) => (
            <FormControl fullWidth>
              <TextField
                fullWidth
                label={label}
                variant="outlined"
                type={isPasswordVisible ? 'text' : 'password'}
                placeholder={customPlaceholder ?? ''}
                error={!!fieldState.error || Boolean(submitError)}
                helperText={
                  <div>
                    {submitError ? (
                      <FormHelperText error>
                        <Localization {...errorMessage} />
                      </FormHelperText>
                    ) : null}
                    {!isShowRequirements && fieldState.error ? (
                      <div {...getTestIdProps(`${name}-password-field-error-message`)}>
                        {renderValidationMessage(fieldState.error, validation?.maxLength, validation?.minLength)}
                      </div>
                    ) : null}
                    {isShowRequirements ? (
                      <div {...getTestIdProps(`${name}-password-field-requirements`)}>
                        <PasswordRequirements
                          minChar={validation?.minLength}
                          requirements={passwordRequirementsArray}
                          validating={isValidatingRequirements}
                          actualValue={field.value as string}
                        />
                      </div>
                    ) : null}
                  </div>
                }
                disabled={!isEditable}
                required={validation?.isMandatory}
                defaultValue={field.value as string}
                inputProps={{
                  maxLength: validation?.maxLength,
                  tabIndex: correctTabIndex,
                  minLength: validation?.minLength ?? undefined,
                  'data-testid': getTestId(`${name}-password-field`),
                }}
                // eslint-disable-next-line react/jsx-no-duplicate-props --- we needed is not duplicate props
                InputProps={{
                  endAdornment: (
                    <PasswordToggle
                      tabIndex={correctTabIndex}
                      isShowPassword={isPasswordVisible}
                      setPasswordVisible={setIsPasswordVisible}
                    />
                  ),
                  autoComplete: 'new-password',
                }}
                onFocus={(_e) => setIsShowRequirements(true)}
                onKeyUp={(_e) => setIsValidatingRequirements(true)}
                {...field}
              />
            </FormControl>
          )}
        />
      </Box>
    </Grid>
  );

  const fieldComponent = isAvailable && renderField();

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment -- we need fragment
    <>{fieldComponent}</>
  );
};
